import { useState, useMemo } from "react"
import { useHistory } from "react-router"
import {
  IPolicyCancelInfoRequestBody,
  IPolicyCancellationReason
} from "contexts/opus/context"
import { useSetPolicyCancellation } from "containers/policies/services/hooks/use-set-policy-cancellation"
import { PaymentPlanType } from "platform-client/types"
import Component from "./component"
import moment, { Moment } from "moment"
import { useMutation } from "react-query"
import useOpus from "contexts/opus/hooks/use-opus"
import { IAdditionalProduct } from "models/policies/additionalProduct"
import { notification } from "antd"
import { useBrokerBackdateLimit } from "containers/policies/services/hooks/use-broker-backdate-limit"
import {
  brokerBackdateLimitMessage,
  isBeforeBrokerBackdateLimit
} from "utils/pocketknife/cancellation-date"

export const PolicyCancellation: React.FC<IPolicyCancellationProps> = ({
  policyID,
  ownerId,
  policyCoverRange,
  policyPurchaseDate,
  paymentPlanType,
  policyLineOfBusiness,
  additionalProducts,
  productBrokerMaxBackdateDays
}) => {
  const history = useHistory()
  const brokerMaxBackdateDays = useBrokerBackdateLimit(
    productBrokerMaxBackdateDays
  )
  const coverRange = useMemo(() => {
    if (policyCoverRange && policyCoverRange.length) {
      return [
        moment.utc(policyCoverRange[0]),
        moment.utc(policyCoverRange[1]).endOf("day")
      ]
    }
  }, [policyCoverRange])

  const minCancellationDate = useMemo(() => {
    if (policyPurchaseDate && policyCoverRange && policyCoverRange.length) {
      const purchaseDate = moment.utc(policyPurchaseDate).startOf("day")
      const coverStart = moment.utc(policyCoverRange[0]).startOf("day")

      if (policyLineOfBusiness === "motor") {
        return coverStart
      } else if (policyLineOfBusiness === "travel") {
        return purchaseDate
      }

      return moment.min([purchaseDate, coverStart])
    }
  }, [policyLineOfBusiness, policyPurchaseDate, policyCoverRange])

  const opusClient = useOpus()

  const defaultDate = useMemo(() => {
    const today = moment.utc().startOf("day")

    if (
      !coverRange ||
      (coverRange &&
        minCancellationDate &&
        coverRange[1].isAfter(today) &&
        (minCancellationDate.isBefore(today) ||
          minCancellationDate.isSame(today)))
    ) {
      return today
    } else if (minCancellationDate && minCancellationDate.isAfter(today)) {
      return minCancellationDate
    }

    return coverRange[1]
  }, [minCancellationDate, coverRange])

  const [cancellationReason, setCancellationReason] = useState<
    IPolicyCancellationReason | undefined
  >(undefined)
  const [effectiveCancellationDate, setEffectiveCancellationDate] =
    useState<Moment>(defaultDate)
  const [proRataRefund, setProRataRefund] = useState<boolean>(false)

  const [additionalProductsToCancel, setAdditionalProductsToCancel] = useState<
    string[]
  >([])

  const cancellationInfoMutation = useMutation(
    (request: IPolicyCancelInfoRequestBody) =>
      opusClient.policyClient.getPolicyCancellationInformation(
        request.policyID,
        request
      )
  )

  const fetchCancellationInfo = (
    cancellationReason: IPolicyCancellationReason | null | undefined,
    effectiveCancellationDate: Moment | null | undefined,
    proRataRefund: boolean | null | undefined,
    additionalProductsToCancel: string[] | null | undefined
  ) => {
    if (policyID) {
      cancellationInfoMutation.mutateAsync({
        policyID: policyID,
        selectedCancellationReason: cancellationReason,
        effectiveCancellationDate: effectiveCancellationDate?.format(),
        requestedCancellationDate: null,
        suppressCancellationEmail: false,
        allowRefund: true,
        refundMethodOverride: proRataRefund === true ? "ProRata" : null,
        additionalProductsToCancel: additionalProductsToCancel ?? []
      })
    }
  }

  const { sendRequest: cancelPolicy, isLoading: isCancellationPending } =
    useSetPolicyCancellation()

  const onCancel = async () => {
    // The picker disables these dates, but the window can move past a
    // selected date if the modal is left open over midnight.
    if (
      brokerMaxBackdateDays !== undefined &&
      isBeforeBrokerBackdateLimit(
        effectiveCancellationDate,
        brokerMaxBackdateDays
      )
    ) {
      notification.error({
        message: brokerBackdateLimitMessage(brokerMaxBackdateDays)
      })
      return
    }

    const requestBody = {
      policyID: policyID as string,
      selectedCancellationReason:
        cancellationReason as IPolicyCancellationReason,
      effectiveCancellationDate: effectiveCancellationDate.format(),
      requestedCancellationDate: null,
      suppressCancellationEmail: false,
      allowRefund: true,
      refundMethodOverride: proRataRefund === true ? "ProRata" : null,
      additionalProductsToCancel: additionalProductsToCancel
    }

    cancelPolicy(ownerId, requestBody).then(() => history.push("/policies"))
  }

  return (
    <Component
      onCancel={onCancel}
      policyRef={cancellationInfoMutation?.data?.policyRef || "-"}
      refundAmount={cancellationInfoMutation?.data?.refundAmount}
      totalClearedPayments={
        cancellationInfoMutation?.data?.totalClearedPayments
      }
      totalRefundsIssued={cancellationInfoMutation?.data?.totalRefundsIssued}
      availableRefund={cancellationInfoMutation?.data?.availableRefund}
      onChangeReason={(reason) => {
        setCancellationReason(reason)
        fetchCancellationInfo(
          reason,
          effectiveCancellationDate,
          proRataRefund,
          additionalProductsToCancel
        )
      }}
      cancellationReason={cancellationReason}
      cancellationReasons={
        cancellationInfoMutation?.data?.cancellationReasons || []
      }
      isButtonLoading={cancellationInfoMutation.isLoading}
      isLayoutLoading={
        cancellationInfoMutation.isLoading || isCancellationPending
      }
      fetchCancellationInfo={fetchCancellationInfo}
      effectiveCancellationDate={effectiveCancellationDate}
      setEffectiveCancellationDate={(date) => {
        setEffectiveCancellationDate(date)
        fetchCancellationInfo(
          cancellationReason,
          date,
          proRataRefund,
          additionalProductsToCancel
        )
      }}
      onProRataRefundChange={(prorata) => {
        setProRataRefund(prorata)
        fetchCancellationInfo(
          cancellationReason,
          effectiveCancellationDate,
          prorata,
          additionalProductsToCancel
        )
      }}
      proRataRefund={proRataRefund}
      coverRange={coverRange}
      minCancellationDate={minCancellationDate}
      brokerMaxBackdateDays={brokerMaxBackdateDays}
      paymentPlanType={paymentPlanType}
      additionalProducts={additionalProducts}
      additionalProductsToCancel={additionalProductsToCancel}
      onAdditionalProductsChange={(selectedProducts) => {
        setAdditionalProductsToCancel(selectedProducts)
        fetchCancellationInfo(
          cancellationReason,
          effectiveCancellationDate,
          proRataRefund,
          selectedProducts
        )
      }}
    />
  )
}

interface IPolicyCancellationProps {
  policyID?: string
  ownerId: string
  policyCoverRange?: string[]
  policyPurchaseDate?: string
  paymentPlanType?: PaymentPlanType
  policyLineOfBusiness?: string
  additionalProducts?: IAdditionalProduct[]
  // The policy's product brokerMaxCancellationBackdateDays setting
  productBrokerMaxBackdateDays?: number
}
